import React, { useState } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import "./DataUpload.css";
import { projectUpload } from '../../../api/project/projectApi';
import { detectstart } from '../../../api/aidetect/detectApi';
import { ensureCsrfToken } from '../../../api/utils/csrfUtils';
import { useProject } from "../../../ProjectContext";

registerPlugin(
  FilePondPluginImagePreview,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize
);

const DataUpload = ({ projectGroupId, projectName, onClose }) => {
  const [folderName, setFolderName] = useState(projectName || "");
  const [folderDesc, setFolderDesc] = useState("");
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = useProject();

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!folderName || !folderDesc || files.length === 0) {
      alert("모든 항목을 입력 및 선택해주세요.");
      return;
    }

    const file = files[0].file;
    setIsLoading(true);
    try {
      await ensureCsrfToken();

      const data = { folderName, folderDesc, projectGroupId };
      const formData = new FormData();
      formData.append("data", JSON.stringify(data));
      formData.append("files", file);

      // 1) 서버 업로드 호출
      const res = await projectUpload(formData);
      console.log("서버 raw 응답:", res);

      // 2) 정규화: 가능한 모든 케이스를 안전하게 파싱
      // - Axios response: res.data may be the server payload
      // - server may return payload as the direct value (res could already be payload)
      const payload = res && res.data ? res.data : res; // prefer res.data if exists

      // payload가 배열 그대로(예: [{...}])인 경우 처리
      // payload가 { status, message, data } 인 경우 data 사용
      let itemsArray = null;
      let message = null;
      let statusCode = null;

      if (Array.isArray(payload)) {
        itemsArray = payload;
      } else if (payload && Array.isArray(payload.data)) {
        itemsArray = payload.data;
        message = payload.message;
        statusCode = payload.status;
      } else if (payload && typeof payload === 'object' && payload.data) {
        // data가 배열 아닌 다른 형태일 경우도 안전하게 처리
        itemsArray = Array.isArray(payload.data) ? payload.data : [payload.data];
        message = payload.message;
        statusCode = payload.status;
      } else {
        // 가능한 포맷들에 해당하지 않음 — 안전하게 경고
        console.warn("응답 정규화 실패 payload:", payload);
      }

      console.log("정규화 결과 itemsArray:", itemsArray, "message:", message, "status:", statusCode);

      // 3) 성공 조건: itemsArray가 존재하고 길이>0
      if (itemsArray && itemsArray.length > 0) {
        const uploaded = itemsArray[0];
        alert(message || "프로젝트 업로드가 완료되었습니다!");
        console.log("업로드된 항목:", uploaded);

        // 4) detectstart 호출 (오류는 개별적으로 캐치)
        try {
          const phoneNumber = userInfo?.tel || ""; // userInfo가 없으면 빈 문자열
          const company = userInfo?.company || "";
          const detectRes = await detectstart(
            uploaded.projectGroupId,
            uploaded.projectId,
            uploaded.projectImageId,
            phoneNumber,
            company
          );
          console.log("detectstart 결과:", detectRes && detectRes.data ? detectRes.data : detectRes);
          alert("AI 분석이 자동으로 시작되었습니다!");
        } catch (detectErr) {
          console.error("detectstart 호출 실패:", detectErr);
          alert("AI 분석 실행 중 오류가 발생했습니다.");
        }
      } else {
        console.warn("예상치 못한 업로드 응답 구조 (정규화 후에도 items 없음):", payload);
        alert("업로드 후 서버 응답이 올바르지 않습니다.");
      }

      onClose();
    } catch (err) {
      console.error("업로드 실패:", err);
      alert("업로드 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='DataUpload_Container'>
      <div className='DataUpload_TitleBox'>데이터 업로드</div>

      <div className='DataUpload_NameContainer'>
        <div className='DataUpload_NameBox'>데이터 이름</div>
        <input
          type='text'
          className='DataUpload_NameTextFelid'
          placeholder='데이터 이름을 입력하세요'
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
        />
      </div>

      <div className='DataUpload_MemoContainer'>
        <div className='DataUpload_NameBox'>데이터 설명</div>
        <input
          type='text'
          className='DataUpload_MemoTextFelid'
          placeholder='데이터 설명을 입력하세요'
          value={folderDesc}
          onChange={(e) => setFolderDesc(e.target.value)}
        />
      </div>

      <div className='DataUpload_UploadContainer'>
        <div className='DataUpload_NameBox'>파일 업로드</div>
        <FilePond
          files={files}
          onupdatefiles={setFiles}
          allowMultiple={false} // ✅ 한 번에 1개만 업로드
          acceptedFileTypes={['image/tiff']} // ✅ tif 형식만 허용
          labelIdle='📁 <span class="filepond--label-action">.tif 파일을 끌어오거나 선택하세요</span>'
          stylePanelAspectRatio="1:0.3"
        />
      </div>

      <div className='DataUpload_BtnContainer'>
        <div
          className='DataUpload_Btn'
          onClick={isLoading ? undefined : handleUpload}
          style={{ opacity: isLoading ? 0.6 : 1 }}
        >
          {isLoading ? "업로드 중..." : "완료"}
        </div>
        <div className='DataUpload_Btn' onClick={onClose}>
          취소
        </div>
      </div>
    </div>
  );
};

export default DataUpload;
